import { useQuery } from "@apollo/client";
import Projects from "entities/ProjectList";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useLazyQuery } from "shared/hooks/useApi";
import graphql from "utils/api/graphql";
import { BiPlus } from "react-icons/bi";
import { FaSearch } from "react-icons/fa";
import { TbFileExport } from "react-icons/tb";
import Button from "shared/components/Button";
import Card from "shared/components/Card";
import Header from "shared/components/Header";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import { QUALIFIERS } from "utils/api/graphql/queries/qualifiers";
import { SOURCES } from "utils/api/graphql/queries/sources";

import { MdFilterList } from "react-icons/md";
import {
  AdvancedFilterPanel,
  FilterChips,
  SavedFiltersBar,
} from "shared/components/AdvancedFilter";
import { NAME_SPACES } from "shared/locales/constants";
import { ACTIONS, INITIAL_VALUES, USER_TYPES } from "utils/constants";
import { ADVANCED_FILTERS, LEAD_TYPES } from "utils/constants/filters";
import { setPaginationHistory } from "utils/helpers/pagination";
import ContactCard from "../../Clients/Card";
import ExportModal from "./ExportModal";
import OfferDrawer from "./Offers";
import SignProjectDrawer from "./SignProjectDrawer";
import Subscriptions from "./Subscriptions";
import ProjectsTable from "./Table";

const List = ({
  filter,
  onSearch,
  applyFilter,
  pagination,
  setPagination,
  createProject,
  actions,
  exportProjects,
  exportLoading,
  sendByEmailLoading,
  showExportModal,
  setShowExportModal,
  handleExportProjects,
  handleSendProjectsByEmail,
  duplicate,
  onDelete,
  onDetail,
  onAssign,
  isAssigning,
  setTracer,
  onChangeStatus,
  tracers,
  setTracers,
  user = {},
  matchStatusesWithProcess,
  advancedFilterProps,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.PROJECT);
  const FILTER = t("FILTER", { returnObjects: true });
  const LIST = t("LIST", { returnObjects: true });
  const PROCESS_TAGS = t("PROCESS_TAGS", { returnObjects: true });
  const ADVANCED_FILTER = t("ADVANCED_FILTER", { returnObjects: true });
  const [openSignDrawer, setOpenSignDrawer] = useState(null);
  const [createOfferDrawer, setCreateOfferDrawer] = useState(null);
  const [openSubscriptionDrawer, setOpenSubscriptionDrawer] = useState(null);
  const [contactId, setContactId] = useState(null);
  const [project, setProject] = useState(null);
  const [search, setSearch] = useState({});
  const [open, setOpen] = useState(false);
  const [offers, setOffers] = useState([]);
  const [status, setStatus] = useState({});

  const { data: qualifiersData } = useQuery(QUALIFIERS);
  const qualifierOptions = (qualifiersData?.qualifiers?.data || []).map(
    ({ name }) => ({ label: name, value: name }),
  );

  const { data: sourcesData } = useQuery(SOURCES);
  const sourceOptions = (sourcesData?.sources?.data || []).map(({ name }) => ({
    label: name,
    value: name,
  }));

  const leadTypeOptions = Object.values(LEAD_TYPES).map((label) => ({
    label,
    value: label,
  }));
  const [searchParams, setSearchParams] = useSearchParams();
  const [getProject, { data: projectFromUrl }] = useLazyQuery(
    graphql.queries.PROJECT,
  );

  useEffect(() => {
    const openContact = searchParams.get("openContact");
    const projectId = searchParams.get("projectId");
    if (!openContact) return;
    setContactId(openContact);
    setOpen(true);
    if (projectId) {
      getProject({ variables: { where: { id: projectId } } });
    } else {
      setProject(null);
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("openContact");
        next.delete("projectId");
        return next;
      },
      { replace: true },
    );
  }, [searchParams, getProject, setSearchParams]);

  useEffect(() => {
    if (projectFromUrl?.project) {
      setProject(projectFromUrl.project);
    }
  }, [projectFromUrl]);

  const fields = [
    {
      key: "OFFER",
      icon: <MdFilterList size={16} />,
      options: tracers.map(({ name }) => name),
      mode: "tags",
      translate: FILTER.OFFER,
      variable: "offers",
    },
    {
      key: "STATUS",
      icon: <MdFilterList size={16} />,
      options: Object.keys(PROCESS_TAGS),
      mode: "tags",
      translate: FILTER.PROCESS_TAG,
      variable: "tags",
    },
  ];

  const operators = [
    { operator: "EQUAL" },
    { operator: "IS_IN" },
    { operator: "START_WITH" },
    { operator: "END_WITH" },
  ];

  const selectionOptions = {
    "statuses.status.tag": Object.keys(PROCESS_TAGS).map((key) => ({
      label: PROCESS_TAGS[key],
      value: key,
    })),
    "tracer.source.name": sourceOptions,
    "contact.fields.leadType": leadTypeOptions,
    "tracer.name": tracers.map(({ name }) => ({
      label: name,
      value: name,
    })),
    "qualification.qualifier.name": qualifierOptions,
  };

  const onCloseSubscription = () => {
    setOpenSubscriptionDrawer(null);
  };

  const onCloseSign = () => {
    setOpenSignDrawer(null);
  };

  const showDrawerSubscription = (id) => {
    setOpenSubscriptionDrawer(id);
  };

  const showDrawerSign = ({ id, statuses }) => {
    setOpenSignDrawer({ id, statuses });
  };

  const PAGINATION_OPTIONS = [
    { label: `10 ${LIST.FILTERS.PROJECTS}`, value: 10 },
    { label: `20 ${LIST.FILTERS.PROJECTS}`, value: 20 },
    { label: `50 ${LIST.FILTERS.PROJECTS}`, value: 50 },
    { label: `100 ${LIST.FILTERS.PROJECTS}`, value: 100 },
  ];
  return (
    <>
      <Header
        key={"header"}
        title={LIST.HEADER.TITLE}
        actions={[
          ...(actions[ACTIONS.ADD_PROJECT]
            ? [
                {
                  icon: <BiPlus size={20} />,
                  text: LIST.HEADER.CREATE.NORMAL,
                  onClick: () => setCreateOfferDrawer(true),
                },
              ]
            : []),
          ...(actions[ACTIONS.EXPORT_SUBSCRIPTIONS]
            ? [
                {
                  icon: <TbFileExport size={20} />,
                  text: LIST.HEADER.EXPORT_PROJECTS,
                  onClick: exportProjects,
                  loading: exportLoading,
                },
              ]
            : []),
        ]}
      />
      <Card type="simple">
        <div className="filter__area">
          <div className="search--bar">
            <Input
              placeholder={LIST.FILTERS.CLIENT_LAST_NAME}
              onChange={({ target: { value } }) =>
                setSearch((prev) => ({ ...prev, clientLastName: value }))
              }
            />
            <Input
              placeholder={LIST.FILTERS.CLIENT_FIST_NAME}
              onChange={({ target: { value } }) =>
                setSearch((prev) => ({ ...prev, clientFirstName: value }))
              }
            />
            {user?.meta?.type === USER_TYPES.INTERNAL_SUPERVISOR.type && (
              <>
                <Input
                  placeholder={LIST.FILTERS.ADVISOR_LAST_NAME}
                  onChange={({ target: { value } }) =>
                    setSearch((prev) => ({ ...prev, advisorLastName: value }))
                  }
                />
                <Input
                  placeholder={LIST.FILTERS.ADVISOR_FIST_NAME}
                  onChange={({ target: { value } }) =>
                    setSearch((prev) => ({ ...prev, advisorFirstName: value }))
                  }
                />
              </>
            )}
            <Button
              type="secondary"
              onClick={() => onSearch(search)}
              icon={<FaSearch size={14} />}
              size={"full--w"}
            >
              {LIST.FILTERS.FILTER}
            </Button>
          </div>
          <div className="filter__search">
            {advancedFilterProps && (
              <AdvancedFilterPanel
                fields={ADVANCED_FILTERS.PROJECT.fields}
                panelFilters={advancedFilterProps.panelFilters}
                setPanelFilters={advancedFilterProps.setPanelFilters}
                appliedFilters={advancedFilterProps.appliedFilters}
                onApply={advancedFilterProps.applyFilters}
                onSave={advancedFilterProps.savePreset}
                context={{
                  isSupervisor:
                    user?.meta?.type === USER_TYPES.INTERNAL_SUPERVISOR.type,
                  isRingEnabled:
                    user?.organization?.config?.aircall?.enabled === true,
                }}
                translate={ADVANCED_FILTER}
                selectionOptions={selectionOptions}
                filterCount={advancedFilterProps.filterCount}
              />
            )}
            <Select
              options={PAGINATION_OPTIONS}
              value={pagination.take || INITIAL_VALUES.take}
              onChange={(value) => {
                setPaginationHistory({
                  footprint: "projects",
                  value: Math.floor(pagination.skip / value) + 1,
                  take: value,
                  count: pagination.count,
                });
                setPagination((prev) => ({ ...prev, take: value }));
              }}
            />
          </div>
        </div>
        {advancedFilterProps && advancedFilterProps.presets.length > 0 && (
          <SavedFiltersBar
            presets={advancedFilterProps.presets}
            activePresetId={advancedFilterProps.activePresetId}
            onApplyPreset={advancedFilterProps.applyPreset}
            onDeletePreset={advancedFilterProps.deletePreset}
            translate={ADVANCED_FILTER}
          />
        )}
        {advancedFilterProps &&
          advancedFilterProps.appliedFilters.length > 0 && (
            <FilterChips
              fields={ADVANCED_FILTERS.PROJECT.fields}
              appliedFilters={advancedFilterProps.appliedFilters}
              onRemoveFilter={advancedFilterProps.removeFilter}
              translate={ADVANCED_FILTER}
              selectionOptions={selectionOptions}
            />
          )}
      </Card>
      {contactId && (
        <ContactCard
          contactId={contactId}
          project={project}
          open={open}
          matchStatusesWithProcess={matchStatusesWithProcess}
          tracers={tracers}
          setTracers={setTracers}
          setOpen={setOpen}
        />
      )}
      <OfferDrawer
        setCreateOfferDrawer={setCreateOfferDrawer}
        createProject={createProject}
        open={createOfferDrawer}
        tracers={tracers}
      />
      <div className="m-bottom-24" />
      <Projects
        View={ProjectsTable}
        setValues={setPagination}
        skip={pagination.skip}
        filter={{ ...filter, ...pagination }}
        fetchPolicy="network-only"
        duplicate={duplicate}
        onDelete={onDelete}
        onDetail={onDetail}
        onAssign={onAssign}
        isAssigning={isAssigning}
        // tracer={tracer}
        setTracer={setTracer}
        showDrawerSubscription={showDrawerSubscription}
        showDrawerSign={showDrawerSign}
        tracers={tracers}
        setContactId={setContactId}
        setProject={setProject}
        setOpen={setOpen}
        onChangeStatus={onChangeStatus}
        actions={actions}
        pagination={pagination}
        matchStatusesWithProcess={matchStatusesWithProcess}
      />
      {openSubscriptionDrawer && (
        <Subscriptions
          onChangeStatus={onChangeStatus}
          onClose={onCloseSubscription}
          tracers={tracers}
          project={openSubscriptionDrawer}
        />
      )}
      {openSignDrawer?.id && (
        <SignProjectDrawer onClose={onCloseSign} project={openSignDrawer} />
      )}
      <ExportModal
        visible={showExportModal}
        onCancel={() => setShowExportModal(false)}
        onConfirm={handleExportProjects}
        onSendByEmail={handleSendProjectsByEmail}
        exportLoading={exportLoading}
        sendByEmailLoading={sendByEmailLoading}
        baseWhere={{}}
      />
    </>
  );
};

export default List;
